import { useState } from "react";

import capitalize from "lodash/capitalize";
import { useHotkeys } from "react-hotkeys-hook";
import { useMediaQuery } from "react-responsive";

import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { IndustryMultiselect } from "@/components/pieces/industries/IndustryMultiselect";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { CtaButton } from "@/components/pieces/onboarding/CtaButton";
import { Form } from "@/components/pieces/onboarding/Form";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import { StyledTag } from "@/components/pieces/StyledTag";
import { usePopularIndustriesQuery } from "@/graphql";
import { Industry } from "@/types/users";

interface IndustriesProps {
  intakeForm: {
    industries: Industry[];
  };
  loading: boolean;
  saveIntakeForm: (data: {
    industryIds: string[];
    industriesSkipped: boolean;
  }) => void;
}

export function Industries({
  intakeForm,
  loading,
  saveIntakeForm,
}: IndustriesProps) {
  const [industries, setIndustries] = useState<Industry[]>(
    intakeForm.industries,
  );
  const { data } = usePopularIndustriesQuery();

  function submit() {
    if (loading) return;

    saveIntakeForm({
      industryIds: industries.map((s) => s.id + ""),
      industriesSkipped: Object.keys(industries).length === 0,
    });
  }
  useHotkeys("enter", submit);

  const popularIndustries = (data?.popularIndustries ?? []).filter(
    (ps) => ps && !industries.find((s) => s.id === ps.id),
  );

  const isXs = useMediaQuery(isXsQuery);

  return (
    <Flex justifyContent="center">
      <Box width={[1, 1, 670]}>
        <FadeInBox>
          <PageTitle
            title="Which industries are relevant?"
            subtitle="Leave blank if you're not sure"
          />
          <Form onSubmit={() => {}}>
            <IndustryMultiselect
              onChange={setIndustries}
              autoFocus={true}
              filters="company_count >= 100"
              large={!isXs}
              disabled={loading}
              selections={industries}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  submit();
                }
              }}
            />
          </Form>

          {popularIndustries.length ? (
            <Box mt={4}>
              <Box mb={2}>Top Industries</Box>
              {popularIndustries.map((ps) => {
                return (
                  <Clickable
                    key={ps?.id}
                    onClick={() => {
                      if (ps) {
                        setIndustries(industries.concat([ps]));
                      }
                    }}
                  >
                    <StyledTag large={!isXs}>
                      {ps?.name.replace(/\w+/g, capitalize)}
                    </StyledTag>
                  </Clickable>
                );
              })}
            </Box>
          ) : null}

          <CtaButton loading={loading} onClick={submit} visible={true}>
            Continue
          </CtaButton>
        </FadeInBox>
      </Box>
    </Flex>
  );
}
